<script lang="ts">
    import { page } from '@inertiajs/svelte';
    import { Button } from '@/components/ui/button';
    import { Server, Home, ArrowLeft, Send, Check, Loader2 } from 'lucide-svelte';
    import axios from 'axios';

    interface Props {
        status?: number;
        message?: string;
        requestId?: string | null;
    }

    let { status = 500, message = 'Internal Server Error', requestId = null }: Props = $props();
    console.log(`[Error 500] ${message} (Request ID: ${requestId})`);

    let sendingReport = $state(false);
    let reportSent = $state(false);
    let sendError = $state<string | null>(null);

    const supportEmail = $derived($page.props.supportEmail as string | null);

    async function sendErrorReport() {
        if (sendingReport || reportSent) return;

        sendingReport = true;
        sendError = null;

        try {
            await axios.post('/web-api/error-report', {
                error_message: `[Server Error] Page load failed with HTTP ${status}: ${message}`,
                page_url: window.location.href,
                error_stack: null,
                request_id: requestId,
            });
            reportSent = true;
        } catch (err) {
            sendError = 'Failed to send report. Please try again.';
            console.error('[Error Report] Failed to send:', err);
        } finally {
            sendingReport = false;
        }
    }
</script>

<div class="flex min-h-screen flex-col items-center justify-center bg-background p-6 text-center">
    <div class="w-full max-w-md">
        <div class="mb-6 flex justify-center">
            <div class="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                <Server class="h-8 w-8 text-destructive" />
            </div>
        </div>

        <h1 class="text-6xl font-bold text-foreground">{status}</h1>
        <p class="mt-4 text-lg text-muted-foreground">{message}</p>
        <p class="mt-2 text-sm text-muted-foreground">
            The server encountered an error and could not complete your request.
        </p>

        {#if requestId}
            <p class="mt-4 font-mono text-xs text-muted-foreground">
                Request ID: {requestId}
            </p>
        {/if}

        <div class="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button variant="default" href="/" class="gap-2">
                <Home class="h-4 w-4" />
                Go Home
            </Button>
            <Button variant="outline" onclick={() => history.back()} class="gap-2">
                <ArrowLeft class="h-4 w-4" />
                Go Back
            </Button>
        </div>

        {#if supportEmail}
            <div class="mt-8 border-t pt-6">
                {#if reportSent}
                    <p class="flex items-center justify-center gap-2 text-sm text-green-600">
                        <Check class="h-4 w-4" />
                        Error report sent successfully
                    </p>
                {:else}
                    <Button
                        variant="ghost"
                        class="w-full text-muted-foreground hover:text-foreground"
                        onclick={sendErrorReport}
                        disabled={sendingReport}
                    >
                        {#if sendingReport}
                            <Loader2 class="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                        {:else}
                            <Send class="mr-2 h-4 w-4" />
                            Send Error Report
                        {/if}
                    </Button>
                    {#if sendError}
                        <p class="mt-2 text-xs text-destructive">{sendError}</p>
                    {/if}
                {/if}
            </div>
        {/if}
    </div>
</div>
